import React from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  ListSubheader,
  Checkbox,
  ListItemText,
  Chip,
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useField } from 'formik';

/**
 * MuiSelect - Select dropdown component
 *
 * Jakob's Law: Standard select behavior
 * Hick's Law: Options limited and grouped for easy scanning
 * Miller's Law: Group options when more than 7 items
 */

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 6,
    '&:hover:not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderWidth: 2,
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.875rem',
  },
  '& .MuiFormHelperText-root': {
    marginLeft: 0,
    marginTop: theme.spacing(0.5),
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  '&.Mui-selected': {
    backgroundColor: `${theme.palette.primary.main}14`,
    '&:hover': {
      backgroundColor: `${theme.palette.primary.main}20`,
    },
  },
}));

const EmptyOption = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

const MuiSelect = ({
  name,
  label,
  placeholder,
  options = [],
  variant = 'outlined',
  size = 'medium',
  fullWidth = true,
  required = false,
  disabled = false,
  multiple = false,
  showCheckbox = false,
  renderChips = false,
  loading = false,
  emptyText = 'No options available',
  groupBy,
  getOptionLabel = (option) => option.label || option.name || option,
  getOptionValue = (option) => option.value ?? option.id ?? option,
  getOptionDisabled = () => false,
  helperText,
  useFormik = true,
  value: controlledValue,
  onChange: controlledOnChange,
  onBlur: controlledOnBlur,
  error: controlledError,
  sx = {},
  menuProps = {},
  ...props
}) => {
  // Try to use Formik context
  let field = {};
  let meta = {};

  try {
    if (useFormik && name) {
      [field, meta] = useField(name);
    }
  } catch (error) {
    // Not inside a Formik context
  }

  const isFormikField = useFormik && name && field.name;

  const value = isFormikField ? field.value : controlledValue;
  const onChange = isFormikField ? field.onChange : controlledOnChange;
  const onBlur = isFormikField ? field.onBlur : controlledOnBlur;
  const error = isFormikField ? meta.touched && Boolean(meta.error) : controlledError;
  const errorText = isFormikField ? (meta.touched && meta.error) : (controlledError ? helperText : null);

  // Ensure value is an array for multiple select
  const selectValue = multiple
    ? (Array.isArray(value) ? value : [])
    : (value ?? '');

  // Group options if groupBy is provided
  const groupedOptions = React.useMemo(() => {
    if (!groupBy) return null;

    const groups = {};
    options.forEach((option) => {
      const group = groupBy(option) || 'Other';
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push(option);
    });
    return groups;
  }, [options, groupBy]);

  const renderValue = (selected) => {
    if (multiple && renderChips && Array.isArray(selected)) {
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {selected.map((val) => {
            const option = options.find((opt) => getOptionValue(opt) === val);
            return (
              <Chip
                key={val}
                label={option ? getOptionLabel(option) : val}
                size="small"
              />
            );
          })}
        </Box>
      );
    }

    if (multiple && Array.isArray(selected)) {
      return selected
        .map((val) => {
          const option = options.find((opt) => getOptionValue(opt) === val);
          return option ? getOptionLabel(option) : val;
        })
        .join(', ');
    }

    const selectedOption = options.find((opt) => getOptionValue(opt) === selected);
    return selectedOption ? getOptionLabel(selectedOption) : selected;
  };

  const renderOptions = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (options.length === 0) {
      return <EmptyOption variant="body2">{emptyText}</EmptyOption>;
    }

    if (groupedOptions) {
      return Object.entries(groupedOptions).map(([group, groupOptions]) => [
        <ListSubheader key={group} sx={{ fontWeight: 600 }}>
          {group}
        </ListSubheader>,
        ...groupOptions.map((option) => renderOption(option)),
      ]);
    }

    return options.map((option) => renderOption(option));
  };

  const renderOption = (option) => {
    const optionValue = getOptionValue(option);
    const optionLabel = getOptionLabel(option);
    const isDisabled = getOptionDisabled(option);
    const isSelected = multiple
      ? selectValue.includes(optionValue)
      : selectValue === optionValue;

    return (
      <StyledMenuItem
        key={optionValue}
        value={optionValue}
        disabled={isDisabled}
      >
        {multiple && showCheckbox && (
          <Checkbox checked={isSelected} sx={{ py: 0 }} />
        )}
        {multiple && showCheckbox ? (
          <ListItemText primary={optionLabel} />
        ) : (
          optionLabel
        )}
      </StyledMenuItem>
    );
  };

  const labelId = `${name}-label`;

  return (
    <StyledFormControl
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      required={required}
      disabled={disabled || loading}
      error={error}
      sx={sx}
    >
      {label && <InputLabel id={labelId}>{label}</InputLabel>}
      <Select
        labelId={labelId}
        name={name}
        value={selectValue}
        onChange={onChange}
        onBlur={onBlur}
        label={label}
        multiple={multiple}
        displayEmpty={!!placeholder}
        renderValue={selectValue === '' && placeholder ? () => (
          <Typography color="text.secondary">{placeholder}</Typography>
        ) : renderValue}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 300,
              borderRadius: 2,
            },
          },
          ...menuProps,
        }}
        {...props}
      >
        {placeholder && !multiple && (
          <StyledMenuItem value="" disabled>
            <Typography color="text.secondary">{placeholder}</Typography>
          </StyledMenuItem>
        )}
        {renderOptions()}
      </Select>
      {(errorText || helperText) && (
        <FormHelperText>{errorText || helperText}</FormHelperText>
      )}
    </StyledFormControl>
  );
};

MuiSelect.propTypes = {
  /** Field name for Formik */
  name: PropTypes.string,
  /** Field label */
  label: PropTypes.string,
  /** Placeholder text */
  placeholder: PropTypes.string,
  /** Array of options */
  options: PropTypes.array.isRequired,
  /** Visual variant */
  variant: PropTypes.oneOf(['outlined', 'filled', 'standard']),
  /** Size of the select */
  size: PropTypes.oneOf(['small', 'medium']),
  /** Full width select */
  fullWidth: PropTypes.bool,
  /** Mark field as required */
  required: PropTypes.bool,
  /** Disable select */
  disabled: PropTypes.bool,
  /** Enable multiple selection */
  multiple: PropTypes.bool,
  /** Show checkboxes in multiple mode */
  showCheckbox: PropTypes.bool,
  /** Render chips for selected values in multiple mode */
  renderChips: PropTypes.bool,
  /** Show loading indicator */
  loading: PropTypes.bool,
  /** Text to show when no options */
  emptyText: PropTypes.string,
  /** Function to group options */
  groupBy: PropTypes.func,
  /** Function to get option label */
  getOptionLabel: PropTypes.func,
  /** Function to get option value */
  getOptionValue: PropTypes.func,
  /** Function to determine if option is disabled */
  getOptionDisabled: PropTypes.func,
  /** Helper text */
  helperText: PropTypes.node,
  /** Use Formik context */
  useFormik: PropTypes.bool,
  /** Controlled value */
  value: PropTypes.any,
  /** Controlled onChange handler */
  onChange: PropTypes.func,
  /** Controlled onBlur handler */
  onBlur: PropTypes.func,
  /** Controlled error state */
  error: PropTypes.bool,
  /** Custom styles */
  sx: PropTypes.object,
  /** Props for Menu component */
  menuProps: PropTypes.object,
};

export default MuiSelect;
